import { parseVariableValue } from '~/common/variable/value';
import { modals } from '@mantine/modals';
import { ActionIcon } from '@mantine/core';
import { IconMaximize } from '@tabler/icons-react';
import { CodeHighlight } from '@mantine/code-highlight';
import { TruncatedText } from '~/components/common/TruncatedText';

export function VariableValueCell({
  name,
  value,
  parseAsJson = false,
}: {
  name: string;
  value: string | undefined | null;
  /** Whether to parse the value as JSON for project variables */
  parseAsJson?: boolean;
}) {
  // Handle empty/null/undefined values - no copy button needed for definitions
  if (value === undefined || value === null) {
    return (
      <div className="py-2">
        <span className="text-sm italic text-gray-400 dark:text-gray-500">undefined</span>
      </div>
    );
  }

  // Handle empty strings for project variables - no copy button needed for definitions
  if (parseAsJson && (!value || value.trim() === '')) {
    return (
      <div className="py-2">
        <span className="text-sm italic text-gray-400 dark:text-gray-500">empty</span>
      </div>
    );
  }

  let displayValue: unknown = value;

  // Parse as JSON for project variables
  if (parseAsJson && typeof value === 'string') {
    displayValue = parseVariableValue(value);
  }

  // Function to open JSON viewer modal
  const openJsonViewer = () => {
    const jsonString = JSON.stringify(displayValue, null, 2);
    modals.open({
      title: 'JSON Value Viewer',
      size: 'xl',
      centered: true,
      children: (
        <div className="max-h-[70vh] overflow-y-auto">
          <CodeHighlight
            code={jsonString}
            language="tsx"
            withCopyButton
            radius="md"
          />
        </div>
      ),
    });
  };

  // Display as JSON if it's an object or array
  if (typeof displayValue === 'object' && displayValue !== null) {
    const jsonString = JSON.stringify(displayValue, null, 2);
    return (
      <div className="py-2">
        <div className="group relative">
          <div className="max-h-[120px] overflow-y-auto">
            <CodeHighlight
              code={jsonString}
              language="tsx"
              withCopyButton={false}
              radius="sm"
            />
          </div>
          <ActionIcon
            variant="default"
            size="sm"
            className="absolute right-4 top-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            onClick={openJsonViewer}
            title="View in large display"
            color="gray"
          >
            <IconMaximize size={14} />
          </ActionIcon>
        </div>
      </div>
    );
  }

  // Display as string
  return (
    <div className="py-2">
      <TruncatedText
        text={String(displayValue)}
        maxLength={100}
        modalTitle={`Variable value for ${name}`}
        showCopyButton={true}
      />
    </div>
  );
}
